/**
 * i18n bootstrap (Phase 4.1). react-i18next + browser language detection.
 *
 * Resources: English (`en`, the source of truth) + Spanish (`es`). Language resolution
 * order: localStorage → navigator → fallback "en". The persisted key is `eddkit.lang`
 * (mirrors the `eddkit.token` convention in api/client.ts). The auth layer additionally
 * syncs the choice to `user.settings.language` server-side via PATCH /api/profile.
 *
 * Keep `en` exactly matching the app's current wording; only labels/prose/buttons/help
 * text are translated — data values, headings/identifiers, format codes and API field
 * names stay as-is.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./en.json";
import es from "./es.json";

export const LANG_STORAGE_KEY = "eddkit.lang";
export const SUPPORTED_LANGUAGES = ["en", "es"] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export function normalizeLanguage(value: unknown): AppLanguage {
  return String(value ?? "").toLowerCase().startsWith("es") ? "es" : "en";
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: "en",
    supportedLngs: ["en", "es"],
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    interpolation: {
      // React already escapes output.
      escapeValue: false,
    },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    returnNull: false,
  });

export default i18n;
